<template>
  <div v-if="isLocalhost" class="env-tag-container" :class="envClass">
    <div class="env-tag-badge" @click="toggleDetails" title="Click to inspect environment details">
      <span class="env-tag-dot"></span>
      <span class="env-tag-label">{{ envLabel }}</span>
    </div>
    <div v-if="showDetails" class="env-tag-details">
      <div class="env-tag-header">
        <strong>Environment Info</strong>
        <span class="env-tag-close" @click.stop="showDetails = false">&times;</span>
      </div>
      <div class="env-tag-row">
        <span class="env-tag-key">Env Target:</span>
        <span class="env-tag-val highlight">{{ envLabel }}</span>
      </div>
      <div class="env-tag-row">
        <span class="env-tag-key">Project ID:</span>
        <span class="env-tag-val">{{ projectId || 'N/A' }}</span>
      </div>
      <div class="env-tag-row">
        <span class="env-tag-key">CSA API:</span>
        <span class="env-tag-val" :title="apiUrl">{{ apiUrlTruncated || 'N/A' }}</span>
      </div>
      <div v-if="useEmulator" class="env-tag-row">
        <span class="env-tag-key">Emulator:</span>
        <span class="env-tag-val text-success">Enabled</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EnvTag",
  data() {
    return {
      showDetails: false,
    };
  },
  computed: {
    isLocalhost() {
      if (typeof window === "undefined") return false;
      const host = window.location.hostname;
      return host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0";
    },
    projectId() {
      return process.env.VUE_APP_PROJECT_ID || "";
    },
    apiUrl() {
      return process.env.VUE_APP_CSA_API_URL || "";
    },
    useEmulator() {
      return process.env.VUE_APP_USE_EMULATOR === "true";
    },
    envType() {
      // 1. Explicit override
      const explicitEnv = (process.env.VUE_APP_ENV || "").toLowerCase();
      if (["local", "dev", "prod"].includes(explicitEnv)) {
        return explicitEnv;
      }
      // 2. Local emulator or local API
      if (
        this.useEmulator ||
        this.apiUrl.includes("localhost") ||
        this.apiUrl.includes("127.0.0.1")
      ) {
        return "local";
      }
      // 3. Dev environment
      if (this.projectId.includes("dev") || this.apiUrl.includes("dev")) {
        return "dev";
      }
      // 4. Production environment
      if (
        this.projectId.includes("1983a") ||
        this.apiUrl.includes("chesssquad.com")
      ) {
        return "prod";
      }
      return "dev";
    },
    envLabel() {
      return this.envType.toUpperCase();
    },
    envClass() {
      return `env-tag--${this.envType}`;
    },
    apiUrlTruncated() {
      if (!this.apiUrl) return "";
      try {
        const url = new URL(this.apiUrl);
        return url.hostname;
      } catch (e) {
        return this.apiUrl;
      }
    },
  },
  methods: {
    toggleDetails() {
      this.showDetails = !this.showDetails;
    },
  },
};
</script>

<style scoped>
.env-tag-container {
  position: fixed;
  top: 10px;
  right: 12px;
  z-index: 999999;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  user-select: none;
}

.env-tag-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  backdrop-filter: blur(4px);
}

.env-tag-badge:hover {
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

.env-tag-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.8);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.85);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Environment Themes */
.env-tag--local .env-tag-badge {
  background: linear-gradient(135deg, #6366f1, #06b6d4);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.env-tag--dev .env-tag-badge {
  background: linear-gradient(135deg, #d97706, #f59e0b);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.env-tag--prod .env-tag-badge {
  background: linear-gradient(135deg, #dc2626, #ef4444);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Details Popup Card */
.env-tag-details {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 220px;
  background: rgba(30, 41, 59, 0.95);
  backdrop-filter: blur(10px);
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);
  font-size: 12px;
  line-height: 1.5;
}

.env-tag-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.env-tag-close {
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: #94a3b8;
}

.env-tag-close:hover {
  color: #ffffff;
}

.env-tag-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.env-tag-key {
  color: #94a3b8;
}

.env-tag-val {
  font-weight: 600;
  color: #e2e8f0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 130px;
}

.env-tag-val.highlight {
  color: #38bdf8;
}

.env-tag-val.text-success {
  color: #4ade80;
}
</style>
